@file:OptIn(androidx.compose.ui.ExperimentalComposeUiApi::class, androidx.compose.foundation.layout.ExperimentalLayoutApi::class)

package com.trevorwiebe.timesheet.settlements.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.AssistChip
import androidx.compose.material3.Card
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.backhandler.BackHandler
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import com.trevorwiebe.timesheet.core.domain.formatPercent
import com.trevorwiebe.timesheet.core.domain.formatWithCommas
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.DraftActionButton
import com.trevorwiebe.timesheet.core.presentation.common.DraftButtonStyle
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveWidth
import com.trevorwiebe.timesheet.core.presentation.common.TimeSheetButton
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.payPeriod.presentation.uiUtils.ChipItem
import com.trevorwiebe.timesheet.settlements.domain.model.Settlement
import com.trevorwiebe.timesheet.settlements.domain.model.SettlementLine
import com.trevorwiebe.timesheet.theme.successGreenText

@Composable
internal fun SettlementDetailContent(settlement: Settlement, state: SettlementsState, openPdf: () -> Unit, onEvent: (SettlementsEvent) -> Unit) {
    BackHandler { onEvent(SettlementsEvent.OnCloseSettlement) }
    val muted = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
    Scaffold(
        topBar = {
            TopBar(
                title = "Settlement ${settlement.numberLabel}",
                backIcon = { BackIcon() },
                onBack = { onEvent(SettlementsEvent.OnCloseSettlement) },
                actions = {
                    TextButton(
                        onClick = {
                            openPdf()
                            onEvent(SettlementsEvent.OnViewPdfClick)
                        },
                        enabled = !state.isGeneratingPdf,
                    ) {
                        Text(
                            text = if (state.isGeneratingPdf) "Preparing PDF…" else "View PDF",
                            color = MaterialTheme.colorScheme.tertiary,
                        )
                    }
                },
            )
        },
        bottomBar = {
            if (settlement.isDraft) {
                ResponsiveWidth {
                    Row(
                        modifier = Modifier.fillMaxWidth().navigationBarsPadding().padding(12.dp),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                        DraftActionButton(
                            text = "Delete draft",
                            style = DraftButtonStyle.DESTRUCTIVE,
                            onClick = { onEvent(SettlementsEvent.OnRequestDelete) },
                        )
                        DraftActionButton(
                            text = "Mark as sent",
                            style = DraftButtonStyle.OUTLINED,
                            loading = state.isMarkingSent,
                            enabled = !state.isMarkingSent,
                            onClick = { onEvent(SettlementsEvent.OnRequestMarkSent) },
                        )
                        DraftActionButton(
                            text = "Email Settlement",
                            style = DraftButtonStyle.FILLED,
                            enabled = state.sendDialog == null,
                            onClick = { onEvent(SettlementsEvent.OnSendClick) },
                        )
                    }
                }
            }
        },
    ) { innerPadding ->
        ResponsiveContent(modifier = Modifier.padding(innerPadding)) {
            LazyColumn(Modifier.fillMaxSize()) {
                item { SettlementHeader(settlement) }
                state.selectedLinesByDriver.forEach { group ->
                    item(key = "driver-${group.driverName}") {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(start = 12.dp, end = 12.dp, top = 12.dp, bottom = 4.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Text(group.driverName, fontSize = 16.sp, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
                            Text("Pay ${group.payFormatted}", fontSize = 14.sp, color = muted)
                        }
                        HorizontalDivider()
                    }
                    items(group.lines, key = { it.loadId }) { line ->
                        SettlementLineRow(
                            line = line,
                            editable = settlement.isDraft,
                            onClick = { onEvent(SettlementsEvent.OnLineClick(line.loadId)) },
                        )
                    }
                }
                item { Totals(settlement) }
                if (!settlement.isCarrier) item { DeductionsSection(settlement, state, onEvent) }
                item {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text("Net pay", fontSize = 18.sp, fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                        Text(settlement.netPayFormatted, fontSize = 18.sp, fontWeight = FontWeight.Bold)
                    }
                    Spacer(Modifier.height(16.dp))
                }
            }
        }
    }

    state.lineDialog?.let { dialog -> state.lineDialogLine?.let { LinePayDialog(it, dialog, onEvent) } }
    state.deductionDialog?.let { DeductionDialog(it, state.deductionSuggestions, onEvent) }
    state.sendDialog?.let { SendSettlementDialog(settlement, it, onEvent) }

    NativeDialog(
        visible = state.infoMessage != null,
        title = state.infoTitle,
        message = state.infoMessage,
        confirmText = "OK",
        onConfirm = { onEvent(SettlementsEvent.OnInfoShown) },
        onDismiss = { onEvent(SettlementsEvent.OnInfoShown) },
    )
    NativeDialog(
        visible = state.showMarkSentConfirm,
        title = "Mark ${settlement.numberLabel} as sent?",
        message = "It moves to Sent and can no longer be changed or deleted: sent settlements are kept for your records.",
        confirmText = if (state.isMarkingSent) "Saving…" else "Mark as sent",
        onConfirm = { if (!state.isMarkingSent) onEvent(SettlementsEvent.OnConfirmMarkSent) },
        dismissText = "Cancel",
        onDismiss = { onEvent(SettlementsEvent.OnDismissMarkSent) },
    )
    NativeDialog(
        visible = state.showDeleteConfirm,
        title = "Delete draft ${settlement.numberLabel}?",
        message = "Its ${settlement.lines.size} load(s) go back to unsettled so you can settle them again.",
        confirmText = if (state.isDeleting) "Deleting…" else "Delete",
        onConfirm = { if (!state.isDeleting) onEvent(SettlementsEvent.OnConfirmDelete) },
        dismissText = "Cancel",
        onDismiss = { onEvent(SettlementsEvent.OnDismissDelete) },
    )
}

@Composable
private fun SettlementHeader(settlement: Settlement) {
    Column(Modifier.fillMaxWidth().padding(12.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = settlement.ownerOperatorName,
                fontSize = 18.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.weight(1f),
            )
            ChipItem(settlement.statusLabel, if (settlement.isDraft) Color.Gray else successGreenText)
        }
        Spacer(Modifier.height(4.dp))
        if (settlement.isCarrier) DetailLine("Pay to", "Carrier")
        DetailLine("Settlement date", Util.toFriendlyDate(settlement.settlementDate))
        DetailLine("Loads", "${settlement.lines.size} • ${periodLabel(settlement)}")
        settlement.sentAt?.let {
            DetailLine("Sent", Util.toFriendlyDate(it) + if (settlement.sentTo.isNotBlank()) " to ${settlement.sentTo}" else "")
        }
    }
    HorizontalDivider()
}

@Composable
private fun DetailLine(label: String, value: String, bold: Boolean = false) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Text(
            label,
            fontSize = 14.sp,
            fontWeight = if (bold) FontWeight.Bold else null,
            color = if (bold) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
            modifier = Modifier.weight(1f),
        )
        Text(value, fontSize = 14.sp, fontWeight = if (bold) FontWeight.Bold else null)
    }
}

@Composable
private fun SettlementLineRow(line: SettlementLine, editable: Boolean, onClick: () -> Unit) {
    val muted = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
    Column(
        Modifier
            .fillMaxWidth()
            .clickable(enabled = editable, onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 8.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = Util.toFriendlyDate(line.date) + if (line.loadNumber.isNotBlank()) "  #${line.loadNumber}" else "",
                fontSize = 14.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.weight(1f),
            )
            Text(line.payFormatted, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
        }
        Text(
            text = "${line.pickup}  to  ${line.drop}",
            fontSize = 13.sp,
            color = muted,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
        )
        val unit = if (line.unitDetail.isNotBlank()) " (${line.unitDetail})" else ""
        PayLine("Base ${cents(line.base)}$unit at ${line.basePayPercent.formatPercent()}%", line.basePay)
        line.charges.forEach {
            PayLine("${it.charge.label} ${it.amountFormatted} at ${it.payPercent.formatPercent()}%", it.pay)
        }
        if (editable) {
            Text(
                text = "Tap to change this load's pay %",
                fontSize = 12.sp,
                color = MaterialTheme.colorScheme.tertiary,
                modifier = Modifier.padding(start = 8.dp, top = 2.dp),
            )
        }
    }
    HorizontalDivider()
}

@Composable
private fun PayLine(label: String, cents: Int) {
    Row {
        Text(label, fontSize = 13.sp, modifier = Modifier.weight(1f).padding(start = 8.dp))
        Text(cents(cents), fontSize = 13.sp)
    }
}

private fun cents(value: Int) = "$" + value.formatCentsAsDollars()

@Composable
private fun SectionTitle(text: String) {
    Text(
        text = text,
        fontSize = 16.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier.padding(start = 12.dp, end = 12.dp, top = 16.dp, bottom = 4.dp),
    )
}

@Composable
private fun Totals(settlement: Settlement) {
    SectionTitle("Totals")
    Column(Modifier.padding(horizontal = 12.dp)) {
        DetailLine("Gross revenue", settlement.grossFormatted)
        if (settlement.isCarrier) {
            DetailLine("Carrier pay", settlement.payFormatted, bold = true)
        } else {
            DetailLine("Miles", if (settlement.miles > 0) settlement.miles.formatWithCommas() else "No mileage logged")
            DetailLine("Gross per mile", settlement.grossPerMileFormatted)
            DetailLine("Driver pay", settlement.payFormatted, bold = true)
            DetailLine("Pay per mile", settlement.payPerMileFormatted)
        }
    }
    Spacer(Modifier.height(8.dp))
    HorizontalDivider()
}

@Composable
private fun DeductionsSection(settlement: Settlement, state: SettlementsState, onEvent: (SettlementsEvent) -> Unit) {
    val muted = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
    Row(verticalAlignment = Alignment.CenterVertically) {
        Column(Modifier.weight(1f)) { SectionTitle("Deductions") }
        if (settlement.isDraft) {
            TextButton(onClick = { onEvent(SettlementsEvent.OnAddDeductionClick) }) {
                Text("Add deduction", color = MaterialTheme.colorScheme.tertiary)
            }
        }
    }
    val totals = state.selectedDeductionTotals
    if (totals.isEmpty()) {
        Text(
            text = "No deductions.",
            fontSize = 14.sp,
            color = muted,
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 4.dp),
        )
    } else {
        Row(Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 4.dp)) {
            Text("", modifier = Modifier.weight(1f))
            Text("This settlement", fontSize = 12.sp, color = muted, modifier = Modifier.width(120.dp), maxLines = 1)
            Text("Year to date", fontSize = 12.sp, color = muted, modifier = Modifier.width(100.dp), maxLines = 1)
        }
        totals.forEach { total ->
            val index = settlement.deductions.indexOfFirst {
                it.description.trim().equals(total.description, ignoreCase = true)
            }
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable(enabled = settlement.isDraft && index >= 0) { onEvent(SettlementsEvent.OnDeductionClick(index)) }
                    .padding(horizontal = 12.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(total.description, fontSize = 14.sp, modifier = Modifier.weight(1f))
                Text(total.thisSettlementFormatted, fontSize = 14.sp, modifier = Modifier.width(120.dp))
                Text(total.yearToDateFormatted, fontSize = 14.sp, color = muted, modifier = Modifier.width(100.dp))
            }
        }
        Row(Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 4.dp)) {
            Text("Total deductions", fontSize = 14.sp, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
            Text(settlement.deductionsTotalFormatted, fontSize = 14.sp, fontWeight = FontWeight.SemiBold, modifier = Modifier.width(220.dp))
        }
    }
    Spacer(Modifier.height(8.dp))
    HorizontalDivider()
}

@Composable
private fun DialogCard(onDismiss: () -> Unit, content: @Composable () -> Unit) {
    Dialog(onDismissRequest = onDismiss) {
        Card(modifier = Modifier.responsiveDialogWidth(), shape = RoundedCornerShape(16.dp)) {
            Column(Modifier.padding(20.dp)) { content() }
        }
    }
}

@Composable
private fun DialogButtons(
    confirmText: String,
    busy: Boolean,
    enabled: Boolean,
    onCancel: () -> Unit,
    onConfirm: () -> Unit,
    onDelete: (() -> Unit)? = null,
) {
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        if (onDelete != null) {
            TextButton(onClick = onDelete, enabled = !busy) {
                Text("Delete", color = MaterialTheme.colorScheme.error)
            }
        }
        Spacer(Modifier.weight(1f))
        TextButton(onClick = onCancel, enabled = !busy) {
            Text("Cancel", color = MaterialTheme.colorScheme.tertiary)
        }
        Spacer(Modifier.width(8.dp))
        TimeSheetButton(onClick = onConfirm, text = confirmText, modifier = Modifier, loading = busy, enabled = enabled && !busy)
    }
}

@Composable
private fun LinePayDialog(line: SettlementLine, dialog: LinePayDialogState, onEvent: (SettlementsEvent) -> Unit) {
    DialogCard(onDismiss = { onEvent(SettlementsEvent.OnDismissLineDialog) }) {
        Text("Pay for this load", fontSize = 20.sp, fontWeight = FontWeight.SemiBold)
        Spacer(Modifier.height(8.dp))
        Text(
            text = "Load ${line.loadNumber.ifBlank { Util.toFriendlyDate(line.date) }}",
            fontSize = 14.sp,
        )
        Spacer(Modifier.height(12.dp))
        TimesheetTextField(
            modifier = Modifier.fillMaxWidth(),
            text = dialog.baseText,
            label = "Base rate ${cents(line.base)} pay (%)",
            singleLine = true,
            selectAllOnFocus = true,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            onTextChange = { onEvent(SettlementsEvent.OnLineBasePercentChanged(it)) },
        )
        dialog.charges.forEach { input ->
            Spacer(Modifier.height(12.dp))
            TimesheetTextField(
                modifier = Modifier.fillMaxWidth(),
                text = input.percentText,
                label = "${input.charge.label} ${input.amountFormatted} pay (%)",
                singleLine = true,
                selectAllOnFocus = true,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                onTextChange = { onEvent(SettlementsEvent.OnLinePercentChanged(input.charge, it)) },
            )
        }
        dialog.error?.let { Text(it, color = MaterialTheme.colorScheme.error, fontSize = 13.sp, modifier = Modifier.padding(top = 6.dp)) }
        Spacer(Modifier.height(16.dp))
        DialogButtons(
            confirmText = "Save",
            busy = dialog.isSaving,
            enabled = dialog.baseText.isNotBlank() && dialog.charges.all { it.percentText.isNotBlank() },
            onCancel = { onEvent(SettlementsEvent.OnDismissLineDialog) },
            onConfirm = { onEvent(SettlementsEvent.OnSaveLinePercent) },
        )
    }
}

@Composable
private fun DeductionDialog(dialog: DeductionDialogState, suggestions: List<String>, onEvent: (SettlementsEvent) -> Unit) {
    DialogCard(onDismiss = { onEvent(SettlementsEvent.OnDismissDeduction) }) {
        Text(if (dialog.index == null) "Add deduction" else "Edit deduction", fontSize = 20.sp, fontWeight = FontWeight.SemiBold)
        Spacer(Modifier.height(12.dp))
        TimesheetTextField(
            modifier = Modifier.fillMaxWidth(),
            text = dialog.description,
            label = "Deduction (e.g. Insurance)",
            singleLine = true,
            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
            onTextChange = { onEvent(SettlementsEvent.OnDeductionDescriptionChanged(it)) },
        )
        if (suggestions.isNotEmpty()) {
            FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                suggestions.take(6).forEach { name ->
                    AssistChip(
                        onClick = { onEvent(SettlementsEvent.OnDeductionDescriptionChanged(name)) },
                        label = { Text(name) },
                    )
                }
            }
        }
        Spacer(Modifier.height(8.dp))
        TimesheetTextField(
            modifier = Modifier.fillMaxWidth(),
            text = dialog.amountText,
            label = "Amount ($)",
            singleLine = true,
            selectAllOnFocus = true,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            onTextChange = { onEvent(SettlementsEvent.OnDeductionAmountChanged(it)) },
        )
        dialog.error?.let { Text(it, color = MaterialTheme.colorScheme.error, fontSize = 13.sp, modifier = Modifier.padding(top = 6.dp)) }
        Spacer(Modifier.height(16.dp))
        DialogButtons(
            confirmText = "Save",
            busy = dialog.isSaving,
            enabled = true,
            onCancel = { onEvent(SettlementsEvent.OnDismissDeduction) },
            onConfirm = { onEvent(SettlementsEvent.OnSaveDeduction) },
            onDelete = if (dialog.index != null) ({ onEvent(SettlementsEvent.OnDeleteDeduction) }) else null,
        )
    }
}

@Composable
private fun SendSettlementDialog(settlement: Settlement, dialog: SendSettlementState, onEvent: (SettlementsEvent) -> Unit) {
    DialogCard(onDismiss = { onEvent(SettlementsEvent.OnDismissSend) }) {
        Text("Email settlement ${settlement.numberLabel}", fontSize = 20.sp, fontWeight = FontWeight.SemiBold)
        Spacer(Modifier.height(8.dp))
        Text(
            text = "Emails the settlement PDF to ${settlement.ownerOperatorName}. Once sent, the settlement can't be changed or deleted.",
            fontSize = 14.sp,
        )
        Spacer(Modifier.height(12.dp))
        TimesheetTextField(
            modifier = Modifier.fillMaxWidth(),
            text = dialog.to,
            label = "Send to",
            singleLine = true,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
            onTextChange = { onEvent(SettlementsEvent.OnSendRecipientChanged(it)) },
        )
        dialog.error?.let { Text(it, color = MaterialTheme.colorScheme.error, fontSize = 13.sp, modifier = Modifier.padding(top = 6.dp)) }
        Spacer(Modifier.height(16.dp))
        DialogButtons(
            confirmText = "Send",
            busy = dialog.isSending,
            enabled = dialog.to.isNotBlank(),
            onCancel = { onEvent(SettlementsEvent.OnDismissSend) },
            onConfirm = { onEvent(SettlementsEvent.OnConfirmSend) },
        )
    }
}
